import React, { useState, useEffect, useCallback } from 'react';
import { useEffectWithDeepCompare } from '@gasbuddy/react-hooks';
import PropTypes from 'prop-types';
import { Header, Loader, Text, Button } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import NoReviews from '../NoReviews';
import ReviewPanel from '../ReviewPanel';
import { noop } from '../../../lib/utils';
import styles from './Reviews.module.css';
import { REVIEWS_PAGE_SIZE } from '../../constants/reviewRatings';

const cx = classnames.bind(styles);

export default function Reviews({
  stationId,
  isFetching,
  hasFetched,
  reviews,
  rating,
  fetchReviewsByStationId,
  hasMoreReviews,
  error,
}) {
  const [offset, setOffset] = useState(0);
  const [visibleReviews, setVisibleReviews] = useState([]);

  const loadMoreReviews = useCallback(() => {
    fetchReviewsByStationId(stationId, Math.ceil(reviews.length / REVIEWS_PAGE_SIZE) + 1);
  }, [fetchReviewsByStationId, stationId, reviews]);

  // When station changes, reset the offset and visible reviews
  useEffect(() => {
    setVisibleReviews([]);
    setOffset(0);
  }, [stationId]);

  // if reviews have not been fetched, fetch them
  // if reviews were fetched then adjust offset and visible reviews
  useEffectWithDeepCompare(() => {
    if (!error && !isFetching) {
      if (!hasFetched) {
        // If we haven't done any fetching yet, then any reviews we may or may not have
        // is inaccurate for what we need on the page. Therefore, go fetch them.
        fetchReviewsByStationId(stationId, 1);
      } else if (reviews?.length) {
        // We have reviews that are proper for this page
        const newOffset = offset + REVIEWS_PAGE_SIZE;
        const addedReviews = reviews.slice(offset, newOffset);
        const newVisibleReviews = [].concat(visibleReviews, addedReviews);
        setVisibleReviews(newVisibleReviews);
        setOffset(newOffset);
      }
    }
  }, [isFetching, stationId, reviews, error, fetchReviewsByStationId, hasFetched]);

  let reviewsContent;
  if (isFetching) {
    reviewsContent = <Loader />;
  } else if (error) {
    reviewsContent = (
      <Text as="p" color="orange">
        {error}
      </Text>
    );
  } else if (visibleReviews?.length) {
    reviewsContent = (
      <React.Fragment>
        <ReviewPanel
          stationId={stationId}
          reviews={visibleReviews}
          rating={rating}
        />
        {hasMoreReviews && (
          <React.Fragment>
            <br />
            <Button
              secondary
              onClick={loadMoreReviews}
              loading={isFetching}
              fluid
            >
              Load More Reviews
            </Button>
          </React.Fragment>
        )}
      </React.Fragment>

    );
  } else {
    reviewsContent = (
      <NoReviews stationId={stationId} />
    );
  }

  return (
    <div className={cx('reviewComponentContainer')}>
      <Header as="h3">
        <span className={cx('reviewsHeader')}>Reviews</span>
      </Header>
      {reviewsContent}
    </div>
  );
}

Reviews.propTypes = {
  stationId: PropTypes.string.isRequired,
  isFetching: PropTypes.bool,
  hasFetched: PropTypes.bool,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      user: PropTypes.shape({
        avatarUrl: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ),
  rating: PropTypes.number,
  fetchReviewsByStationId: PropTypes.func,
  error: PropTypes.string,
  hasMoreReviews: PropTypes.bool,
};

Reviews.defaultProps = {
  isFetching: false,
  hasFetched: false,
  reviews: undefined,
  rating: 0,
  fetchReviewsByStationId: noop,
  error: undefined,
  hasMoreReviews: false,
};
